/**
 * Videos Feed – YouTube-style grid of published Studio videos (server uploads).
 * Thumbnails, title, view count. No AT Protocol.
 */

import {useCallback, useState} from 'react'
import {
  Image,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {cleanError} from '#/lib/strings/errors'
import {useStudioPublishedVideosQuery} from '#/state/queries/studio'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotate'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import type {StudioVideo} from '#/lib/api/studio'
import {formatViewCount} from '#/lib/strings/view-count'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'VideosFeed'>

const CARD_ASPECT = 16 / 9

function VideosFeedCard({video}: {video: StudioVideo}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation()
  const thumbUri = video.thumbnail_path || video.video_path

  const onPress = useCallback(() => {
    navigation.navigate('StudioVideo', {videoId: video.id})
  }, [navigation, video.id])

  return (
    <Pressable
      onPress={onPress}
      style={[
        a.overflow_hidden,
        a.rounded_md,
        Platform.OS === 'web' && {cursor: 'pointer'},
      ]}
      accessibilityRole="button"
      accessibilityLabel={_(msg`Open video`)}>
      <View style={{width: '100%', aspectRatio: CARD_ASPECT}}>
        {thumbUri ? (
          Platform.OS === 'web' ? (
            <img
              src={thumbUri}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
            />
          ) : (
            <Image
              source={{uri: thumbUri}}
              style={[a.flex_1, {width: '100%', height: '100%'}]}
              resizeMode="cover"
            />
          )
        ) : (
          <View
            style={[
              a.flex_1,
              a.justify_center,
              a.align_center,
              t.atoms.bg_contrast_50,
            ]}>
            <VideoIcon width={40} fill={t.atoms.text_contrast_low.color} />
          </View>
        )}
      </View>
      <View style={[a.p_sm]}>
        <Text
          style={[a.text_sm, a.font_semi_bold, t.atoms.text]}
          numberOfLines={2}>
          {video.title || _(msg`Untitled`)}
        </Text>
        <Text
          style={[a.text_xs, a.pt_xs, t.atoms.text_contrast_medium]}
          numberOfLines={1}>
          {formatViewCount(video.total_views)} <Trans>views</Trans>
        </Text>
      </View>
    </Pressable>
  )
}

function VideosFeedGrid({videos}: {videos: StudioVideo[]}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.flex_row,
        a.flex_wrap,
        a.px_lg,
        a.pb_xl,
        {gap: 12},
      ]}>
      {videos.map(video => (
        <View
          key={video.id}
          style={[
            a.rounded_md,
            t.atoms.bg_contrast_25,
            {
              width: '47%',
              maxWidth: 320,
              flexGrow: 1,
              flexBasis: 140,
            },
          ]}>
          <VideosFeedCard video={video} />
        </View>
      ))}
    </View>
  )
}

export function VideosFeedScreen({}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const [isPTRing, setIsPTRing] = useState(false)

  const {
    data: videos = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStudioPublishedVideosQuery()

  const onRefresh = useCallback(async () => {
    setIsPTRing(true)
    try {
      await refetch()
    } finally {
      setIsPTRing(false)
    }
  }, [refetch])

  const cardWidth = gtMobile ? 280 : 160

  return (
    <Layout.Screen testID="videosFeedScreen">
      <Layout.Header.Outer>
        <Layout.Header.MenuButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Videos</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
      </Layout.Header.Outer>
      {videos.length === 0 && !isLoading ? (
        <Layout.Center>
          {isError ? (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.atoms.text_contrast_low.color} />
              <Text
                style={[
                  a.pt_md,
                  a.pb_sm,
                  a.text_2xl,
                  a.font_semi_bold,
                  t.atoms.text,
                ]}>
                <Trans>Whoops!</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                  {maxWidth: 360},
                ]}>
                {cleanError(error) || _(msg`Failed to load videos`)}
              </Text>
              <Button
                label={_(msg`Retry`)}
                size="small"
                color="secondary_inverted"
                variant="solid"
                onPress={() => refetch()}>
                <ButtonText>
                  <Trans>Retry</Trans>
                </ButtonText>
                <ButtonIcon icon={RetryIcon} position="right" />
              </Button>
            </View>
          ) : (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.palette.primary_500} />
              <Text
                style={[
                  a.pt_md,
                  a.pb_sm,
                  a.text_2xl,
                  a.font_semi_bold,
                  t.atoms.text,
                ]}>
                <Trans>No videos yet</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>Videos uploaded to Studio will appear here.</Trans>
              </Text>
            </View>
          )}
        </Layout.Center>
      ) : (
        <ScrollView
          style={[a.flex_1]}
          contentContainerStyle={[a.pb_2xl]}
          refreshControl={
            Platform.OS !== 'web' ? (
              <RefreshControl
                refreshing={isPTRing}
                onRefresh={onRefresh}
              />
            ) : undefined
          }
        >
          <Layout.Center style={[a.w_full]}>
            {isLoading ? (
              <View style={[a.p_3xl, a.align_center]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>Loading…</Trans>
                </Text>
              </View>
            ) : (
              <VideosFeedGrid videos={videos} />
            )}
          </Layout.Center>
        </ScrollView>
      )}
    </Layout.Screen>
  )
}
